import { LegAnsokanFormFieldName, LegAnsokanField, Yrke } from './LegAnsokanForm.types';
import { AnsokanFormStepOneModel, LegAnsokan, AnsokanFormTexts, EducationLocation } from '../../models/ansokan.model';
import { FormFieldFile } from 'sos-common/js/commonTypes';
import { FileExtendedDetails } from 'sos-common/js/commonTypes/fileupload.types';
import { ValidationObject } from 'sos-common/js/commonTypes/validation.types';
import { SelectBoxOption } from 'sos-common/js';
import { isDateValid } from 'sos-common/js/utils/date';
import { LEG_FILE_MIMETYPES } from '../FilKomplettering/FilKomplettering.mimetypes';
import { validateCentury, validateUtbildningstid } from '../../services/yrkestitelansokanform.validation.service';
import { MultiSelectItem } from 'sos-common/js/commonTypes/multiSelect.types';

// export const EMAIL_NOT_MATCHING = 'E-postadressen stämmer inte överens.';
// export const MANDATORY = 'Den här uppgiften måste fyllas i för att du ska kunna gå vidare.';

export class FieldMap {
  private _map: Map<LegAnsokanFormFieldName | string, BaseField>;
  public allFieldsAreValid: boolean = false;
  private _emailNoMatch: string;
  private _yearOrderError: string;

  constructor(
    model: AnsokanFormStepOneModel,
    ansokan: LegAnsokan,
    formTexts: AnsokanFormTexts,
    educationLocation: EducationLocation,
    translations: (key: string, defaultValue?: string) => string,
    onAdd: (field: FormFieldFile) => void,
    onRemove: (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => void,
    addMultiSelectItem: (selectedItems: MultiSelectItem[]) => void,
  ) {
    const map = new Map();
    this._map = map;
    this._emailNoMatch = translations('errorMessages/emailNoMatch');
    this._yearOrderError =
      translations('errorMessages/graduationBeforeStart') || 'Examensår får inte vara tidigare än startår.';
    map.set(
      LegAnsokanFormFieldName.Email,
      new EpostField(LegAnsokanFormFieldName.Email, ansokan?.Epost, true, translations),
    );

    map.set(
      LegAnsokanFormFieldName.ConfirmEmail,
      new EpostField(LegAnsokanFormFieldName.ConfirmEmail, ansokan?.Epost, false, translations),
    );

    map.set(
      LegAnsokanFormFieldName.Telefonnummer,
      new TelefonnummerField(LegAnsokanFormFieldName.Telefonnummer, ansokan?.Telefonnummer, translations),
    );

    map.set(
      LegAnsokanFormFieldName.DateOfLegitimationCertificate,
      new DateOfLegitimationCertificate(
        LegAnsokanFormFieldName.DateOfLegitimationCertificate,
        ansokan?.Utbildning.DatumForLegitimeradOmYrkesbehorighet,
        translations,
        educationLocation === EducationLocation.EducatedInEuEes ||
          educationLocation === EducationLocation.EducatedOutsideEuEes3year,
        educationLocation === EducationLocation.EducatedInEuEes ||
          educationLocation === EducationLocation.EducatedOutsideEuEes3year,
      ),
    );

    if (educationLocation === EducationLocation.EducatedOutsideEuEesAssessment) {
      map.set(
        LegAnsokanFormFieldName.Diarienummer,
        new DiarienummerField(
          LegAnsokanFormFieldName.Diarienummer,
          ansokan?.Utbildning?.DiarienummerTidigareAnsokan,
          translations,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.AssessmentEducationAfterHighSchool,
        new RequiredBooleanField(
          LegAnsokanFormFieldName.AssessmentEducationAfterHighSchool,
          translations,
          'granskning/educationAfterHighSchool',
          ansokan?.Utbildning?.EftergymnasialUtbildning,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.AssessmentDegree,
        new OptionalTextField(
          LegAnsokanFormFieldName.AssessmentDegree,
          translations,
          'granskning/degree',
          ansokan?.Utbildning?.Utbildningstitel,
          true,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.AssessmentEducationPlace,
        new OptionalTextField(
          LegAnsokanFormFieldName.AssessmentEducationPlace,
          translations,
          'granskning/educationPlace',
          ansokan?.Utbildning?.LarosateNamn,
          true,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.AssessmentVocationalYears,
        new OptionalNumberField(
          LegAnsokanFormFieldName.AssessmentVocationalYears,
          translations,
          'granskning/vocationalYears',
          ansokan?.Utbildning?.Utbildningslangd,
          true,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.AssessmentStartYear,
        new OptionalYearField(
          LegAnsokanFormFieldName.AssessmentStartYear,
          translations,
          'granskning/startYear',
          ansokan?.Utbildning?.UtbildningPaborjad,
          true,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.AssessmentGraduationYear,
        new OptionalYearField(
          LegAnsokanFormFieldName.AssessmentGraduationYear,
          translations,
          'granskning/graduationYear',
          ansokan?.Utbildning?.Examensar,
          true,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.AssessmentEducationCountry,
        new OptionalTextField(
          LegAnsokanFormFieldName.AssessmentEducationCountry,
          translations,
          'granskning/educationCountry',
          ansokan?.Utbildning?.UtbildningslandNamn,
          true,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.AssessmentWorkedAfterGraduation,
        new RequiredBooleanField(
          LegAnsokanFormFieldName.AssessmentWorkedAfterGraduation,
          translations,
          'granskning/workedAfterGraduation',
          ansokan?.Utbildning?.ArbetatEfterExamen,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.AssessmentAllowContact,
        new RequiredBooleanField(
          LegAnsokanFormFieldName.AssessmentAllowContact,
          translations,
          'granskning/allowContact',
          ansokan?.Utbildning?.TillatelseKontaktaSkolaMyndighet,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.AssessmentDenyReason,
        new AssessmentDenyReasonField(
          LegAnsokanFormFieldName.AssessmentDenyReason,
          translations,
          ansokan?.Utbildning?.MotiveringEjKontaktaSkolaMyndighet,
          ansokan?.Utbildning?.TillatelseKontaktaSkolaMyndighet === false,
        ),
      );
    }

    if (educationLocation === EducationLocation.EducatedOutsideEuEes) {
      map.set(
        LegAnsokanFormFieldName.Diarienummer,
        new DiarienummerField(
          LegAnsokanFormFieldName.Diarienummer,
          ansokan?.Utbildning?.DiarienummerTidigareAnsokan,
          translations,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.PaidAssessmentFeeEarlier,
        new RequiredBooleanField(
          LegAnsokanFormFieldName.PaidAssessmentFeeEarlier,
          translations,
          'eServiceForm/paidAssessmentFeeEarlier',
          ansokan?.Utbildning?.BetaltVidGranskning,
        ),
      );

      map.set(
        LegAnsokanFormFieldName.PaymentDate,
        new PaymentDateField(
          LegAnsokanFormFieldName.PaymentDate,
          ansokan?.Utbildning?.DatumForTidigareBetalning,
          translations,
          ansokan?.Utbildning?.BetaltVidGranskning === true,
        ),
      );

      if (model.OccupationId === Yrke.Lakare) {
        map.set(
          LegAnsokanFormFieldName.CompletedSupplementaryEducation,
          new RequiredBooleanField(
            LegAnsokanFormFieldName.CompletedSupplementaryEducation,
            translations,
            'eServiceForm/completedSupplementaryEducation',
            ansokan?.Utbildning?.GjortKompletterandeUtbildning,
          ),
        );
      }
    }

    if (educationLocation === EducationLocation.EducatedInSweden) {
      map.set(
        LegAnsokanFormFieldName.SchoolSelect,
        new LaroverkField(
          LegAnsokanFormFieldName.SchoolSelect,
          model.AvailableSchools,
          ansokan.Utbildning?.LarosateId,
          educationLocation === EducationLocation.EducatedInSweden,
          translations,
        ),
      );
    }

    if (educationLocation === EducationLocation.EducatedInEuEes) {
      map.set(
        LegAnsokanFormFieldName.UtbildningsLand,
        new UtbildningsLand(
          LegAnsokanFormFieldName.UtbildningsLand,
          model?.UtbildningsLander,
          ansokan.Utbildning.UtbildningslandId,
          educationLocation === EducationLocation.EducatedInEuEes,
          educationLocation === EducationLocation.EducatedInEuEes,
          translations,
        ),
      );
    }

    if (
      educationLocation === EducationLocation.EducatedInEuEes ||
      educationLocation === EducationLocation.EducatedOutsideEuEes3year
    ) {
      map.set(
        LegAnsokanFormFieldName.EducationLength,
        new EducationLength(
          LegAnsokanFormFieldName.EducationLength,
          ansokan?.Utbildning?.Utbildningslangd,
          true,
          translations,
        ),
      );
    }

    if (
      educationLocation === EducationLocation.EducatedInEuEes ||
      educationLocation === EducationLocation.EducatedOutsideEuEes3year
    ) {
      map.set(
        LegAnsokanFormFieldName.EducationTitle,
        new EducationTitle(
          LegAnsokanFormFieldName.EducationTitle,
          ansokan?.Utbildning?.Utbildningstitel,
          true,
          translations,
        ),
      );
    }
    if (educationLocation == EducationLocation.EducatedInSweden) {
      map.set(
        LegAnsokanFormFieldName.ExaminationDate,
        new ExaminationDateField(
          LegAnsokanFormFieldName.ExaminationDate,
          ansokan?.Utbildning?.ExamensDatum,
          translations,
        ),
      );
    }

    if (
      educationLocation === EducationLocation.EducatedInEuEes ||
      educationLocation === EducationLocation.EducatedOutsideEuEes3year
    ) {
      map.set(
        LegAnsokanFormFieldName.LegitimationOtherCountry,
        new HasLegInOtherCountry(
          LegAnsokanFormFieldName.LegitimationOtherCountry,
          translations,
          ansokan.Utbildning.legitimationFranAnnatLand,
          true,
          LegAnsokanFormFieldName.LegitimationOtherCountry.toString(),
        ),
      );
    }

    if (
      educationLocation === EducationLocation.EducatedInEuEes ||
      educationLocation === EducationLocation.EducatedOutsideEuEes3year
    ) {
      map.set(
        LegAnsokanFormFieldName.LegitimationAndraLanderId,
        new LegitimationOtherCountryId(
          LegAnsokanFormFieldName.LegitimationAndraLanderId,
          model?.UtbildningsLander,
          ansokan.Utbildning.LegitimationAndraLander,
          true,
          translations,
          ansokan.Utbildning.legitimationFranAnnatLand === true,
          addMultiSelectItem,
        ),
      );
    }

    if (
      educationLocation === EducationLocation.EducatedInEuEes ||
      educationLocation === EducationLocation.EducatedOutsideEuEes3year ||
      educationLocation === EducationLocation.EducatedOutsideEuEes
    ) {
      map.set(
        LegAnsokanFormFieldName.ExaminationYear,
        new ExaminationYearField(
          LegAnsokanFormFieldName.ExaminationYear,
          ansokan?.Utbildning.Examensar,
          translations,
          true,
        ),
      );
    }
    map.set(
      LegAnsokanFormFieldName.CheckboxValue,
      new CheckboxField(LegAnsokanFormFieldName.CheckboxValue, translations, ansokan?.CheckboxValue),
    );

    if (educationLocation === EducationLocation.EducatedInSweden) {
      let showPrimaryFileUpload = true;
      if (model.OccupationId === Yrke.Fysioterapeut && ansokan?.CheckboxValue) {
        showPrimaryFileUpload = false;
      }
      const primaryFieldState = new FilesField(
        LegAnsokanFormFieldName.PrimaryFiles,
        model.PrimaryFileUploadText?.Label,
        ansokan?.PrimaryFiles,
        onAdd,
        onRemove,
        translations,
        showPrimaryFileUpload,
      );

      map.set(LegAnsokanFormFieldName.PrimaryFiles, primaryFieldState);
    }

    if (model.FileUploadText1 !== null) {
      const fileupload1FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload1,
        model.FileUploadText1?.Label,
        ansokan?.Fileupload1Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText1.Required,
        model.FileUploadText1.Show,
      );
      map.set(LegAnsokanFormFieldName.FileUpload1, fileupload1FieldState);
    }
    if (model.FileUploadText2 !== null) {
      const fileupload2FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload2,
        model.FileUploadText2?.Label,
        ansokan?.Fileupload2Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText2.Required,
        model.FileUploadText2.Show,
      );
      map.set(LegAnsokanFormFieldName.FileUpload2, fileupload2FieldState);
    }

    if (model.FileUploadText7 !== null) {
      const fileupload7FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload7,
        model.FileUploadText7?.Label,
        ansokan?.Fileupload7Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText7.Required,
        model.FileUploadText7.Show,
      );
      map.set(LegAnsokanFormFieldName.FileUpload7, fileupload7FieldState);
    }

    if (model.FileUploadText3 !== null) {
      const fileupload3FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload3,
        model.FileUploadText3?.Label,
        ansokan?.Fileupload3Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText3.Required,
        model.FileUploadText3.Show,
      );
      map.set(LegAnsokanFormFieldName.FileUpload3, fileupload3FieldState);
    }

    if (model.FileUploadText4 !== null) {
      const fileupload4FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload4,
        model.FileUploadText4?.Label,
        ansokan?.Fileupload4Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText4.Required,
        model.FileUploadText4.Show,
      );
      map.set(LegAnsokanFormFieldName.FileUpload4, fileupload4FieldState);
    }

    if (model.FileUploadText5 !== null) {
      const fileupload5FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload5,
        model.FileUploadText5?.Label,
        ansokan?.Fileupload5Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText5.Required,
        model.FileUploadText5.Show,
      );
      map.set(LegAnsokanFormFieldName.FileUpload5, fileupload5FieldState);
    }
    if (model.FileUploadText6 !== null) {
      const fileupload6FieldState = new FilesField(
        LegAnsokanFormFieldName.FileUpload6,
        model.FileUploadText6?.Label,
        ansokan?.Fileupload6Files,
        onAdd,
        onRemove,
        translations,
        model.FileUploadText6.Required,
        model.FileUploadText6.Show,
      );
      map.set(LegAnsokanFormFieldName.FileUpload6, fileupload6FieldState);
    }

    if (educationLocation === EducationLocation.EducatedInSweden) {
      let showSecondaryFileUpload = false;
      if (
        model.OccupationId === Yrke.Psykoterapeut ||
        (model.OccupationId === Yrke.HalsoOchSjukvardskurator && ansokan?.CheckboxValue)
      ) {
        showSecondaryFileUpload = true;
      }

      const secondaryFieldState = new FilesField(
        LegAnsokanFormFieldName.SecondaryFiles,
        model.SecondaryFileUploadText?.Label,
        ansokan?.SecondaryFiles,
        onAdd,
        onRemove,
        translations,
        showSecondaryFileUpload,
      );
      map.set(LegAnsokanFormFieldName.SecondaryFiles, secondaryFieldState);
    }
    map.set(
      LegAnsokanFormFieldName.TermsAndConditions,
      new TermsAndConditionsField(LegAnsokanFormFieldName.TermsAndConditions, formTexts.SamtyckeLabel, translations),
    );
  }

  setGotSjukgymnastExamination = () => {
    this.toggleShowFileFor(LegAnsokanFormFieldName.PrimaryFiles);
  };

  toggleShowFileFor = (fieldName: LegAnsokanFormFieldName) => {
    const filesField = this._map.get(fieldName);
    if (!filesField) return;
    filesField.ShowField = !filesField.ShowField;
  };

  showFieldFor = (fieldName: LegAnsokanFormFieldName): boolean => {
    const field = this._map.get(fieldName);
    return !!field && field.ShowField;
  };

  addOnChangeEventHandler = (inputChanged, onChangeTermsAndCondition) => {
    this._map.forEach((base) => {
      base.props.onChange =
        base.props.fieldName === LegAnsokanFormFieldName.TermsAndConditions ? onChangeTermsAndCondition : inputChanged;
    });
  };

  get = (fieldName: LegAnsokanFormFieldName | string): BaseField => this._map.get(fieldName);

  clone = () => {
    this._map = new Map(this._map);
    return this;
  };

  removeAllNonExistingFields = () => {
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.PrimaryFiles);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.SecondaryFiles);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload1);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload2);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload3);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload4);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload5);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload6);
    this._removeFilesWithNullValueId(LegAnsokanFormFieldName.FileUpload7);
  };

  private _removeFilesWithNullValueId = (fileFieldName: LegAnsokanFormFieldName) => {
    const field = this._map.get(fileFieldName);
    if (!field) return;
    const fileField = field.props;
    fileField.value = fileField?.value?.filter((file) => !!file.FileId);
  };

  /**
   * Does not actually deletes the file but just clears the state, sort of.
   */
  removeFilesByTag = (tag: LegAnsokanFormFieldName.PrimaryFiles | LegAnsokanFormFieldName.SecondaryFiles) => {
    const field = this._map.get(tag);
    if (!field) return;
    const fileField = field.props;
    fileField.value = [];
  };

  setFileValueByFieldName = (field: FormFieldFile) => {
    const fieldInMap = this._map.get(field.fieldName);
    if (!fieldInMap) return;
    const fileField = fieldInMap.props;
    fileField.value = field.value;
  };

  setErrorMessage = (message: string, tag: string) => {
    var fieldState = this._map.get(tag);
    if (message) {
      fieldState.props.errorMessage = message;
    }
  };

  setFileErrorMessage = (message: string, tag: string) => {
    var examinationFileState = this._map.get(tag);
    examinationFileState.props.errorMessage = message;
  };

  validateAllFields() {
    this.allFieldsAreValid = true;
    for (let key of Array.from(this._map.keys())) {
      const field = this._map.get(key);
      if (!field.ShowField) continue;
      const result = field.validate();
      if (!result.isValid) {
        this.allFieldsAreValid = false;
        return;
      }
    }
    const emailsAreSame = this.validateEmailComparison();
    this.allFieldsAreValid = emailsAreSame && this.isYearOrderValid();
  }

  validateAllFieldsWithErrorMessages() {
    this.allFieldsAreValid = true;
    for (let key of Array.from(this._map.keys())) {
      const field = this._map.get(key);
      if (!field.ShowField) continue;
      const isValid = this.validateFieldWithErrorMessage(field);
      if (!isValid) {
        this.allFieldsAreValid = false;
      }
    }
    if (!this.validateYearOrder()) this.allFieldsAreValid = false;
  }

  validateFieldWithErrorMessage = (field: BaseField): boolean => {
    let fieldIsValid = field.validateWithErrorMessage();
    let emailsAreSame = field.shouldValidateUsingOtherFields ? this.validateEmailComparison() : true;

    let yearOrderIsValid = true;
    if (
      field.props?.fieldName === LegAnsokanFormFieldName.AssessmentStartYear ||
      field.props?.fieldName === LegAnsokanFormFieldName.AssessmentGraduationYear
    ) {
      yearOrderIsValid = this.validateYearOrder();
    }

    const isValid = fieldIsValid && emailsAreSame && yearOrderIsValid;
    if (!isValid) this.allFieldsAreValid = false;
    return isValid;
  };

  private isYearOrderValid = (): boolean => {
    const start = parseInt(this._map.get(LegAnsokanFormFieldName.AssessmentStartYear)?.props?.value, 10);
    const graduation = parseInt(this._map.get(LegAnsokanFormFieldName.AssessmentGraduationYear)?.props?.value, 10);
    return isNaN(start) || isNaN(graduation) || graduation >= start;
  };

  validateYearOrder = (): boolean => {
    const graduationField = this._map.get(LegAnsokanFormFieldName.AssessmentGraduationYear);
    if (!graduationField?.props) return true;

    const isValid = this.isYearOrderValid();
    if (!isValid) {
      graduationField.props.errorMessage = this._yearOrderError;
      this.allFieldsAreValid = false;
    } else if (graduationField.props.errorMessage === this._yearOrderError) {
      graduationField.props.errorMessage = '';
    }
    return isValid;
  };

  validateEmailComparison = (): boolean => {
    const emailField = this._map.get(LegAnsokanFormFieldName.Email);
    const confirmEmailField = this._map.get(LegAnsokanFormFieldName.ConfirmEmail);

    const areDifferent = confirmEmailField.props.value && emailField.props.value !== confirmEmailField.props.value;
    confirmEmailField.props.errorMessage = areDifferent ? this._emailNoMatch : '';
    return !areDifferent;
  };
}

class BaseField {
  protected _props;
  public ShowField;
  protected _Translations: (key: string, defaultValue?: string) => string;
  constructor(
    fieldName: LegAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    mandatory: boolean = true,
    showField: boolean = true,
  ) {
    this._props = {
      fieldName: fieldName,
      isRequired: mandatory,
      pattern: null,
      patternMessage: '',
      value: null, //NOTE: Value have different types in subtypes.
      errorMessage: '',
      infoMessage: '', //Everything should not be handled as an error but more like information
      actAsNumber: false,
      maxLength: null,
      showMaxLengthMessage: false,
      checkboxWithProps: false,
    };
    this.ShowField = showField;
    this._Translations = translations;
  }

  get props(): LegAnsokanField {
    return this._props;
  }

  validate = (): ValidationObject => {
    const props = this._props;
    const result = {
      isValid: true,

      errorMessage: '',
    };

    result.isValid = this.validateExists();
    if (!result.isValid) {
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    result.isValid = this.validatePattern();
    if (!result.isValid) {
      result.errorMessage = props.patternMessage;
      return result;
    }
    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;

    const result = this.validate();
    props.errorMessage = result.errorMessage;

    return result.isValid;
  };

  protected validateExists = (): boolean => {
    const props = this._props;
    const valueExists = !!props.value;
    return props.isRequired && valueExists;
  };

  protected validatePattern = (): boolean => {
    const props = this._props;
    const valueExists = !!props.value;
    if (!props.pattern || !valueExists) return true;
    return new RegExp(props.pattern).test(props.value);
  };

  get shouldValidateUsingOtherFields() {
    return false;
  }
}

class ExaminationYearField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    value: string,
    translations: (key: string, defaultValue?: string) => string,
    showField: boolean = true,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.label = `${translations('graduationYear')} ${translations('mandatory')}`;
    props.value = value ?? '';
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    const props = this._props;

    if (!this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }
    const error = validateCentury(props.value, this._Translations);

    if (error != '') {
      result.isValid = false;
      result.errorMessage = error;
    }

    return result;
  };

  validateExists = () => {
    const props = this._props;
    return props.isRequired && props.value.length > 0;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;

    const result = this.validate();
    props.errorMessage = result.errorMessage;

    return result.isValid;
  };
}

class HasLegInOtherCountry extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    value?: boolean,
    showField: boolean = true,
    ariaLabel?: string,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.isRequired = false;
    props.label = `${translations('legitimationsAnsokan/otherCountryLegitimation')} ${translations('mandatory')}`;
    props.value = value;
    props.ariaLabel = ariaLabel;
  }
  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    const props = this._props;

    if (props.value !== true && this.props.value !== false) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    return result;
  };
}

class LegitimationOtherCountryId extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    countries: SelectBoxOption[],
    value: string[],
    showField: boolean,
    translations: (key: string, defaultValue?: string) => string,
    isRequired?: boolean,
    addMultiSelectItem?: (selectedItems: MultiSelectItem[]) => void,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.items = this.mapCountriesToMultiSelectItems(countries, value);
    props.label = `${translations('ifHasOtherCountryText')} ${translations('mandatory')}`;
    props.value = value ?? '';
    props.isRequired = isRequired;
    props.enableShowAllOption = false;
    props.showRemoveSelectionsButton = false;
    props.showAllOptionText = false;
    props.textBoxLabelText = `${translations('ifHasOtherCountryText')} ${translations('mandatory')}`;
    props.onSelectedItemsChange = addMultiSelectItem;
  }

  mapCountriesToMultiSelectItems = (countries: SelectBoxOption[], selectedValues: string[]): MultiSelectItem[] => {
    return countries.map(
      (country) =>
        ({
          key: country.key,
          value: country.text,
          isSelectedByDefault: selectedValues.includes(country.key),
        } as MultiSelectItem),
    );
  };

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };
    const props = this._props;
    if (props.isRequired) {
      if (props.value.length < 1) {
        result.isValid = false;
        result.errorMessage = this._Translations('errorMessages/required');
        return result;
      }
    }

    if (props.value.length > 5) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/onlyFiveCountries');
      return result;
    }

    return result;
  };
}

class EducationTitle extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    value: string,
    showField: boolean = true,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.label = `${translations('educationName')} ${translations('mandatory')}`;
    props.value = value ?? '';
  }
}

class DateOfLegitimationCertificate extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    examensDatum: string,
    translations: (key: string, defaultValue?: string) => string,
    mandatory: boolean = true,
    showField: boolean = true,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.value = examensDatum ?? '';
    props.label = `${translations('licenseFrom')}`;
    props.id = fieldName;
    props.isRequired = mandatory;
  }

  validate = (): ValidationObject => {
    const props = this._props;

    const result = {
      isValid: true,
      errorMessage: '',
    };

    if (props.value == '') return result;

    //validate format
    const pattern = /^\d\d\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
    if (!pattern.test(props.value)) {
      (result.errorMessage = this._Translations('errorMessages/correctDateFormat')), (result.isValid = false);
      return result;
    }

    //validate date
    const valueSplit = props.value.split('-');
    if (!isDateValid(valueSplit[0], valueSplit[1], valueSplit[2])) {
      (result.errorMessage = this._Translations('errorMessages/correctDateError')), (result.isValid = false);
      return result;
    }

    //can't be higher than today
    if (Date.parse(props.value) > Date.now()) {
      (result.errorMessage = this._Translations('errorMessages/FutureDateError')), (result.isValid = false);
      return result;
    }

    // var temp = valueSplit[0];
    var enteredYear = parseInt(valueSplit[0], 10);
    if (enteredYear < 1900) {
      //Borde egentligen konfigureras och ett mer korrekt äldsta datum sättas. Men detta fångar de mest uppenbara felfallen.
      (result.errorMessage = this._Translations('errorMessages/oldDateError')), (result.isValid = false);
      return result;
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();

    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class EducationLength extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    value: string,
    showField: boolean = true,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.label = `${translations('educationLength')} ${translations('mandatory')}`;
    props.isRequired = true;
    props.value = value ?? '';
  }
  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };
    const props = this._props;

    if (!this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    const validationError = validateUtbildningstid(props.value, 99, this._Translations);
    if (validationError != '') {
      result.isValid = false;
      result.errorMessage = validationError;
      return result;
    }
    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();
    props.errorMessage = result.errorMessage;
    return result.isValid;
  };

  validateExists = () => {
    const props = this._props;
    return props.isRequired && props.value.length > 0;
  };
}

class EpostField extends BaseField {
  protected _show: boolean = true;
  private _shouldValidate: boolean;
  constructor(
    fieldName: LegAnsokanFormFieldName,
    value: string,
    shouldValidate: boolean = true,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.id = fieldName;
    props.autoComplete = 'email';
    props.label = `${translations('email')} ${translations('mandatory')}`;
    props.value = value ?? '';
    props.pattern =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zåäöA-ZÅÄÖ\-0-9]+\.)+[a-zåäöA-ZÅÄÖ]{2,}))$/;
    props.patternMessage = translations('errorMessages/emailWrongFormat');

    this._shouldValidate = shouldValidate;
  }

  get shouldValidateUsingOtherFields() {
    return true;
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    if (!this._shouldValidate) return result;

    const props = this._props;

    if (!this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    let maxLength = 255;
    if (props.value.length > maxLength) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/toBigString');
      return result;
    }
    if (!this.validatePattern()) {
      result.isValid = false;
      result.errorMessage = props.patternMessage;
      return result;
    }

    const disallowedChars = ['å', 'Å', 'ä', 'Ä', 'ö', 'Ö'];
    for (let index = 0; index < disallowedChars.length; index++) {
      const char = disallowedChars[index];
      if (props.value.includes(char)) {
        result.isValid = false;
        result.errorMessage = this._Translations('errorMessages/disallowedChars');
        return result;
      }
    }

    return result;
  };

  validateExists = () => {
    const props = this._props;
    return props.isRequired && props.value.length > 0;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;

    const result = this.validate();
    props.errorMessage = result.errorMessage;

    return result.isValid;
  };
}

class UtbildningsLand extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    countries: SelectBoxOption[],
    EuCountry: string,
    showField: boolean,
    isRequired: boolean,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    (props.label = `${translations('educationCountry')} ${translations('mandatory')}`),
      (props.defaultOptionText = translations('defaultOptionText'));
    props.className = 'm-b-2';
    props.options = countries.filter((x) => x.key !== '200043');
    props.value = EuCountry ?? '';
    props.isRequired = isRequired;
  }
}

class LaroverkField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    availableSchools: SelectBoxOption[],
    larosateId: string,
    showField: boolean = true,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations, showField);
    const props = this._props;
    props.label = `${translations('universityDropdownLabel')} ${translations('mandatory')}`;
    props.defaultOptionText = translations('select');
    props.className = 'm-b-2';
    props.options = availableSchools;
    props.value = larosateId ?? '';
  }
}

class CheckboxField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    value?: boolean,
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.fieldName = LegAnsokanFormFieldName.CheckboxValue;
    props.isRequired = false;
    props.errorMessage = '';
    props.value = value;
  }

  validate = (): ValidationObject => ({
    isValid: true,
    errorMessage: '',
  });

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();

    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class ExaminationDateField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    examensDatum: string,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.value = examensDatum ?? '';
    props.id = fieldName;
  }

  validate = (): ValidationObject => {
    const props = this._props;

    const result = {
      isValid: true,
      errorMessage: '',
    };

    if (!this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    //validate format
    const pattern = /^\d\d\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
    if (!pattern.test(props.value)) {
      result.errorMessage = this._Translations('errorMessages/correctDateFormat');
      result.isValid = false;
      return result;
    }

    //validate date
    const valueSplit = props.value.split('-');
    if (!isDateValid(valueSplit[0], valueSplit[1], valueSplit[2])) {
      result.errorMessage = this._Translations('errorMessages/correctDateError');
      result.isValid = false;
      return result;
    }

    //can't be higher than today
    if (Date.parse(props.value) > Date.now()) {
      result.errorMessage = this._Translations('errorMessages/FutureDateError');
      result.isValid = false;
      return result;
    }

    // var temp = valueSplit[0];
    var enteredYear = parseInt(valueSplit[0], 10);
    if (enteredYear < 1900) {
      //Borde egentligen konfigureras och ett mer korrekt älsta datum sättas. Men detta fångar de mest uppenbara felfallen.
      result.errorMessage = this._Translations('errorMessages/oldDateError');
      result.isValid = false;
      return result;
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();

    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class FilesField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    label: string,
    value: FileExtendedDetails[],
    onAdd: (field: FormFieldFile) => void,
    onRemove: (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => void,
    translations: (key: string, defaultValue?: string) => string,
    mandatory: boolean = false,
    showField: boolean = true,
  ) {
    super(fieldName, translations, mandatory, showField);
    const props = this._props;
    props.heading = label ?? '';
    props.max = 10;
    props.showMaxFilesInfoMessage = true;
    props.disabled = false;
    props.nofilesText = translations('noFileUploaded');
    props.onAdd = onAdd;
    props.onRemove = onRemove;
    props.errorMessage = null;
    props.value = value;
    props.mimetype = LEG_FILE_MIMETYPES;
    props.upload = translations('uploadFileText');
    props.maxFileSizeErrorMessage = translations('maxFileSizeErrorMessage');
    props.maxFilesInfoMessage = translations('maxFilesInfoMessage');
    props.illegalFileNameErrorMessage = translations('illegalFileNameErrorMessage');
    props.removeText = translations('removeText');
    props.removeFileText = translations('removeFileText');
    props.removeFilePopupBody = translations('removeFilePopupBody');
    props.abortText = translations('abortText');
    props.closeText = translations('closeText');
  }

  validate = (): ValidationObject => {
    let result = {
      isValid: true,
      errorMessage: '',
    };

    if (this._props.isRequired && !this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();

    props.errorMessage = result.errorMessage;

    return result.isValid;
  };

  validateExists = () => {
    const props = this._props;
    return props.value.length > 0;
  };
}

class TermsAndConditionsField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    label: string,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.label = label;
    props.value = false;
    //props.autoComplete = 'off';
  }

  validate = (): ValidationObject => ({
    isValid: this.props.value,
    errorMessage: '',
  });
  validateWithErrorMessage = (): boolean => {
    const result = this.validate();

    this.props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class DiarienummerField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    value: string,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations, false);
    const props = this._props;
    props.id = fieldName;
    props.label = translations('granskning/caseNumber');
    props.value = value ?? '';
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };
    const props = this._props;

    if (!props.value) return result;

    const pattern = /^\d{1,6}[-/](19|20)\d\d$/;
    if (!pattern.test(props.value)) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/diarienummerFormat');
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();
    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class OptionalTextField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    labelKey: string,
    value?: string,
    isRequired: boolean = false,
  ) {
    super(fieldName, translations, isRequired);
    const props = this._props;
    props.id = fieldName;
    props.label = isRequired ? `${translations(labelKey)} ${translations('mandatory')}` : translations(labelKey);
    props.value = value ?? '';
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    if (this._props.isRequired && !this._props.value) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const result = this.validate();
    this._props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class TelefonnummerField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    value: string,
    translations: (key: string, defaultValue?: string) => string,
  ) {
    super(fieldName, translations, false);
    const props = this._props;
    props.id = fieldName;
    props.autoComplete = 'tel';
    props.label = translations('phoneNumber');
    props.value = value ?? '';
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };
    const props = this._props;

    if (!props.value) return result;

    if (!/^[+]?[\d\s-]{1,20}$/.test(props.value)) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/phoneWrongFormat');
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();
    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class OptionalNumberField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    labelKey: string,
    value?: string,
    isRequired: boolean = false,
  ) {
    super(fieldName, translations, isRequired);
    const props = this._props;
    props.id = fieldName;
    props.label = isRequired ? `${translations(labelKey)} ${translations('mandatory')}` : translations(labelKey);
    props.value = value ?? '';
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };
    const props = this._props;

    if (props.isRequired && !props.value) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    if (!props.value) return result;

    if (!/^\d+$/.test(props.value.replace(/\s/g, ''))) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/onlyNr');
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();
    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class OptionalYearField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    labelKey: string,
    value?: string,
    isRequired: boolean = false,
  ) {
    super(fieldName, translations, isRequired);
    const props = this._props;
    props.id = fieldName;
    props.label = isRequired ? `${translations(labelKey)} ${translations('mandatory')}` : translations(labelKey);
    props.value = value ?? '';
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };
    const props = this._props;

    if (props.isRequired && !props.value) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    if (!props.value) return result;

    const error = validateCentury(props.value, this._Translations);
    if (error != '') {
      result.isValid = false;
      result.errorMessage = error;
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();
    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class OptionalBooleanField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    labelKey: string,
    value?: boolean,
  ) {
    super(fieldName, translations, false);
    const props = this._props;
    props.label = translations(labelKey);
    props.value = value;
    props.ariaLabel = translations(labelKey);
  }

  validate = (): ValidationObject => ({
    isValid: true,
    errorMessage: '',
  });

  validateWithErrorMessage = (): boolean => {
    const result = this.validate();
    this._props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class RequiredBooleanField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    labelKey: string,
    value?: boolean,
  ) {
    super(fieldName, translations);
    const props = this._props;
    props.label = `${translations(labelKey)} ${translations('mandatory')}`;
    props.value = value;
    props.ariaLabel = translations(labelKey);
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };

    if (this.props.value !== true && this.props.value !== false) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const result = this.validate();
    this._props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class AssessmentDenyReasonField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    translations: (key: string, defaultValue?: string) => string,
    value?: string,
    isRequired: boolean = false,
  ) {
    super(fieldName, translations, isRequired);
    const props = this._props;
    props.id = fieldName;
    props.label = `${translations('granskning/denyReason')} ${translations('mandatory')}`;
    props.value = value ?? '';
    props.multiline = true;
    props.classNames = 'height240px';
    props.maxLength = 4000;
    props.showMaxLengthMessage = true;
  }

  validate = (): ValidationObject => {
    const result = {
      isValid: true,
      errorMessage: '',
    };
    const props = this._props;

    if (props.isRequired && !props.value) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();
    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}

class PaymentDateField extends BaseField {
  constructor(
    fieldName: LegAnsokanFormFieldName,
    paymentDate: string,
    translations: (key: string, defaultValue?: string) => string,
    showField: boolean = false,
  ) {
    super(fieldName, translations, true, showField);
    const props = this._props;
    props.id = fieldName;
    props.label = `${translations('eServiceForm/paymentDate')} ${translations('mandatory')}`;
    props.value = paymentDate ?? '';
  }

  validate = (): ValidationObject => {
    const props = this._props;
    const result = {
      isValid: true,
      errorMessage: '',
    };

    if (!this.validateExists()) {
      result.isValid = false;
      result.errorMessage = this._Translations('errorMessages/required');
      return result;
    }

    // Validate format YYYY-MM-DD
    const pattern = /^\d\d\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
    if (!pattern.test(props.value)) {
      result.errorMessage = this._Translations('errorMessages/correctDateFormat');
      result.isValid = false;
      return result;
    }

    // Validate date
    const valueSplit = props.value.split('-');
    if (!isDateValid(valueSplit[0], valueSplit[1], valueSplit[2])) {
      result.errorMessage = this._Translations('errorMessages/correctDateError');
      result.isValid = false;
      return result;
    }

    return result;
  };

  validateWithErrorMessage = (): boolean => {
    const props = this._props;
    const result = this.validate();
    props.errorMessage = result.errorMessage;
    return result.isValid;
  };
}
